import React from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation, faCircleInfo } from "@fortawesome/pro-solid-svg-icons";

import { AnswerDto, AnswerListOverviewDto } from "../../../../types/CompanyAnswersTypes";

import Alert from "../../../../ui-elements/Alert/Alert";
import DisplayBox from "../../../../ui-elements/DisplayBox/DisplayBox";

import { insertDash, getTableDate, getTimeFromDate } from "../../../../services/utils";
import Link from "../../../../ui-elements/Link/Link";
import { getFileIcon } from "../../../../ui-elements/FileUploadDropZone/FileUploadDropZone";
import Tooltip from "../../../../ui-elements/Tooltip/Tooltip";

// Shared utilities — same helpers used by CompanyAnswers
import { buildAnswerText, getOptionTextAnswer } from "../shared/answerHelpers";
import QuestionBlock from "../shared/QuestionBlock";
import { availableVersions, getQuestionOrder, QuestionListType } from "../Constants";


interface IProps {
    data: AnswerListOverviewDto | null;
    answerListIndex: number;
    questionListVersion: availableVersions;
    questionListType: QuestionListType;
}

const IndividualAnswers: React.FC<IProps> = ({ data, answerListIndex, questionListVersion, questionListType }) => {
    const userFiles = data?.answerLists[answerListIndex].userFiles || [];
    const answerList = data?.answerLists[answerListIndex];
    const answers = answerList?.answers || [];

    const fileUploadError = data?.answerLists[answerListIndex].fileUploadError || false;
    const fileUploadErrorMessage = data?.answerLists[answerListIndex].fileUploadErrorMessage;
    const QUESTION_ORDER = getQuestionOrder(questionListVersion, questionListType);

    // ─────────────────────────────────────────────────────────────────────────
    // V1 answer renderer
    // ─────────────────────────────────────────────────────────────────────────

    const renderAnswerV1 = (isWrong: boolean, answerBoolean: boolean | null, answerText: string) => {
        const text = buildAnswerText(answerBoolean, answerText);
        return (
            <div className="answer-wrapper">
                <div className="answer-wrapper__text">
                    {isWrong &&
                        <FontAwesomeIcon className="fa-icon" icon={faCircleExclamation} />
                    }
                    <p className="paragraph-small--bold answer-text">
                        {text}
                    </p>
                </div>
            </div>
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Answer display text (V2)
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Resolves the display text for an individual answer.
     * Handles the beneficial owner special case where name and SSN are combined.
     */
    const getAnswerDisplayText = (item: AnswerDto): string => {
        // Beneficial owner question combines the owner's name and SSN
        if (item.mainQuestionOrder === QUESTION_ORDER.BENEFICIAL_OWNERS) {
            const beneficialOwnerText = [item.beneficialOwner?.name, item.beneficialOwner?.ssn]
                .filter(Boolean)
                .join(' - ');
            return buildAnswerText(item.isWrong ? null : item.answerBoolean, beneficialOwnerText);
        }

        // Questions with predefined options (radio/select answers)
        const optionText = getOptionTextAnswer(item);
        if (optionText !== null) return optionText;

        return buildAnswerText(item.answerBoolean, item.answertext);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Authentication alert helpers
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Builds the alert text describing when and how the user completed the
     * questionnaire. Prefers eLogin timestamp, falls back to finished timestamp.
     */
    const getAlertText = (): string => {
        if (answerList?.userELoginTimestamp) {
            return `svaraði þessari könnun með rafrænni innskráningu í gegnum ${answerList.islandIsLogin ? 'island.is' : 'Auðkenni'} þann
                ${getTableDate(answerList.userELoginTimestamp, 'DD/MM/YYYY', '.')}
                kl. ${getTimeFromDate(answerList.userELoginTimestamp, 'HH:MM:SS')} `;
        }

        return `svaraði þessari könnun þann
                ${getTableDate(answerList?.finishedTimestamp, 'DD/MM/YYYY', '.')}
                kl. ${getTimeFromDate(answerList?.finishedTimestamp, 'HH:MM:SS')} `;
    };

    /** Builds the expanded section showing Auðkenni tracking info when available. */
    const getExpandedText = (): JSX.Element | undefined => {
        if (!answerList?.audkenniTrackId) return undefined;

        return (
            <div>
                <p>
                    Track ID: {answerList.audkenniTrackId}<br />
                    Veitt af: Auðkenni hf.<br />
                </p>
            </div>
        );
    };

    /** Builds the header text including the user's SSN when available. */
    const getHeadText = (): string => {
        if (answerList?.userSSN) {
            return `${answerList.userName}, kt. ${insertDash(answerList.userSSN ?? '')}`;
        }
        return `${answerList?.userName}`;
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Uploaded files section
    // ─────────────────────────────────────────────────────────────────────────

    /** Renders the uploaded files/attachments list with error handling. */
    const renderUploadedFiles = () => {
        if (fileUploadError) {
            return <div className="uploaded-file-list">
                <h5>Fylgiskjöl</h5>
                <Alert type={"error"} headText={"Villa"} text={fileUploadErrorMessage
                    ?? 'Ekki tókst að hlaða upp öllum skjölum með þessum spurningalista. Vinsamlegast hafðu samband við þennan viðskiptavin til að kalla eftir þeim skjölum sem vantar.'} />
            </div>
        }
        if (!userFiles || userFiles.length === 0) return null

        return (
            <div className="uploaded-file-list">
                <h5>Fylgiskjöl</h5>
                {userFiles.map((file, index) => (
                    <div className='uploaded-file-list__item' key={index}>
                        <div className='uploaded-file-list__item--left-section'>
                            <p className='file-item__index'>{index + 1}.</p>
                            <Link
                                url={`/DueDiligence/GetFile/${file.id}`}
                                icon={getFileIcon(file.fileExtensionType)}
                                className='uploaded-file-list__name'
                            >
                                {file.encodedFileName}
                            </Link>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    // V1 Layout: Original flex-based question/answer layout
    // ─────────────────────────────────────────────────────────────────────────

    const renderV1Layout = () => (
        <div className="answers-wrapper">
            <div className="answers-header">
                <h5 id="questions-overview">Spurningar</h5>
                <h5 id="answers-overview">Svör</h5>
            </div>
            {answers.slice(0, -1).map((item, index) => (
                <div key={index} className='question-item'>
                    <div className="question-content">
                        <p className="paragraph-small--bold question-index">{item.order}.</p>
                        <div className="question-texts">
                            <p className="paragraph-small--bold question-title">{item.question}</p>
                            {item.questionDescription && (
                                <p className="paragraph-small--italic question-description">
                                    {item.questionDescription}
                                </p>
                            )}
                        </div>
                    </div>
                    {renderAnswerV1(item.isWrong, item.answerBoolean, item.answertext)}
                </div>
            ))}
        </div>
    );

    // ─────────────────────────────────────────────────────────────────────────
    // V2 Layout: Grid-based layout using shared QuestionBlock
    // ─────────────────────────────────────────────────────────────────────────

    const renderV2Layout = () => (
        <div className="question-answer-container">
            <div className="answers-header">
                <h5 id="questions-overview">Spurningar</h5>
                <h5 id="answers-overview">Svör</h5>
            </div>

            {answers.slice(0, -1).map((item, index) => {
                const isQuestion8 = item.mainQuestionOrder === 8;

                return (
                    <QuestionBlock
                        key={index}
                        question={item}
                        index={index}
                        answerDisplayText={getAnswerDisplayText(item)}
                        showDescriptionRow={item.mainQuestionOrder !== 3 && !isQuestion8}
                        titleTooltip={
                            isQuestion8 && item.questionDescription ? (
                                <Tooltip
                                    noUnderline
                                    text={<FontAwesomeIcon icon={faCircleInfo} className="fa-icon fa-icon--info tooltip-icon" />}
                                    tooltip={item.questionDescription}
                                />
                            ) : undefined
                        }
                    />
                );
            })}
        </div>
    );

    // ─────────────────────────────────────────────────────────────────────────
    // Main render
    // ─────────────────────────────────────────────────────────────────────────

    return (
        <DisplayBox
            id="company-answers"
            title={"Svör við áreiðanleikakönnun"}
        >
            <div className="content">
                {/* Authentication alert — shows who answered and when */}
                <Alert
                    type={"success"}
                    headText={getHeadText()}
                    text={getAlertText()}
                    expandedText={getExpandedText()}
                />

                {questionListVersion === "2" ? renderV2Layout() : renderV1Layout()}

                {/* Uploaded files section */}
                {renderUploadedFiles()}
            </div>
        </DisplayBox>
    );
};

export default IndividualAnswers;
